import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { ROLES } from '../../config/menuData.js';
import { money } from '../../utils/money.js';
import { TRADING_STATUSES, tradingStatusForFlag } from '../../config/tradingStatus.js';
import SearchableSelect from '../../components/SearchableSelect.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';

// 9021 / 9023: no Pilotage Required switch and no tug count — free-text remarks instead.
const EMPTY = {
  customerUserId: '',
  vesselId: '',
  portId: '',
  berthId: '',
  // 8949: the service booked replaces the movement type; the area picks the tariff.
  serviceId: '',
  assistanceAreaId: '',
  tradingStatus: '',
  scheduledAt: '',
  remarks: '',
};

// 9022 / 9005: office-only overrides, kept apart from the booking details so
// entering them does not clear the quote.
const NO_OVERRIDES = {
  indicativeOverride: '',
  indicativeOverrideReason: '',
  overrideConflict: false,
  conflictOverrideReason: '',
};

const EMPTY_RECUR = {
  customerUserId: '',
  vesselId: '',
  portId: '',
  berthId: '',
  serviceId: '',
  assistanceAreaId: '',
  tradingStatus: '',
  remarks: '',
  intervalDays: '7',
  firstRun: '',
};

const REMARKS_PLACEHOLDER = 'e.g. 2 tugs requested, bow thruster inoperative';

/** A datetime-local input value for a stored timestamp. */
function toLocalInput(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const toOptions = (rows) => (rows || []).map((r) => ({ value: r.id, label: r.name }));

function fmt(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return dt;
  }
}

function statusBadge(status) {
  if (status === 'Confirmed') return 'bg-success';
  if (status === 'Submitted') return 'bg-info text-dark';
  return 'bg-secondary';
}

/** 8902: TUGS column — the dispatched tugs by name, else the number requested,
 * else that the dispatcher has not assigned any yet. */
function tugsLabel(b) {
  if (b.assigned_tugs) return b.assigned_tugs;
  // 8948: one tug per job generated for the booking.
  if (b.job_count) return `${b.job_count} job${b.job_count > 1 ? 's' : ''} (1 tug each) — pending assignment`;
  if (b.requested_tug_count) {
    return `${b.requested_tug_count} requested — pending assignment`;
  }
  return 'Pending assignment';
}

/** 8948: the jobs a service generates, as the booking form previews them.
 * 9023: the service's own tug counts, else as advised for the vessel. */
function jobsPreview(service) {
  if (!service) return '';
  return service.jobs
    .map((j) => {
      const tugs = j.tugCount || null;
      return `${j.jobName} × ${tugs ? `${tugs} tug${tugs > 1 ? 's' : ''}` : 'tugs advised for the vessel'}`;
    })
    .join(' · ');
}

export default function CustomerBookingPage() {
  const { user, hasRole } = useAuth();
  // Office roles see every customer's bookings; owner-only actions stay with the customer.
  const isOffice = hasRole(ROLES.ADMIN, ROLES.DISPATCHER, ROLES.BOOKING);
  const isOwn = (b) => !isOffice || b.customer_user_id === user?.id;
  const canCancel = (b) => isOwn(b) || hasRole(ROLES.DISPATCHER);
  // 8880: the booking desk clears arrival for bookings it raised on a customer's behalf.
  const canClear = (b) => isOwn(b) || hasRole(ROLES.BOOKING, ROLES.DISPATCHER);
  const [bookings, setBookings] = useState([]);
  const [reference, setReference] = useState({
    vessels: [],
    ports: [],
    berths: [],
    services: [],
    assistanceAreas: [],
    movementTypes: [],
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [overrides, setOverrides] = useState(NO_OVERRIDES);
  // 9005: the vessel's booking that clashes with this one (from the server's 409).
  const [conflict, setConflict] = useState(null);
  const [errors, setErrors] = useState({});
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Office staff book on a customer's behalf: the vessels offered are that customer's (8858).
  const [customerVessels, setCustomerVessels] = useState([]);

  // Final arrival clearance.
  const [clearance, setClearance] = useState(null); // the booking being confirmed
  // 9026: no Pilotage Required here — the booking keeps whatever it has.
  const [clearanceForm, setClearanceForm] = useState({
    aisArrivalWindow: '',
    captainReadiness: false,
  });
  const [clearanceError, setClearanceError] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Live execution tracking.
  const [tracking, setTracking] = useState(null); // execution snapshot
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Standing orders (FR-BKG-5).
  const [recurring, setRecurring] = useState([]);
  const [recur, setRecur] = useState(EMPTY_RECUR);
  // 8914: office staff set up a standing order for a chosen customer, on that customer's vessels.
  const [recurCustomerVessels, setRecurCustomerVessels] = useState([]);
  const [recurMsg, setRecurMsg] = useState(null);
  const [savingRecur, setSavingRecur] = useState(false);
  // 9025: the standing order being edited, and the one about to be deleted.
  const [editingRecur, setEditingRecur] = useState(null);
  const [deletingRecur, setDeletingRecur] = useState(null);
  const [recurBusy, setRecurBusy] = useState(false);
  const trackMapEl = useRef(null);
  const trackMapRef = useRef(null);

  const openTracking = async (booking) => {
    setPageMsg(null);
    setTracking(null);
    setTrackingLoading(true);
    try {
      const exec = await api.getBookingExecution(booking.id);
      setTracking(exec);
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to load live execution.' });
    } finally {
      setTrackingLoading(false);
    }
  };

  // The map effect's cleanup owns map.remove(); removing it here too threw on
  // the second remove and blanked the page (8814/8815).
  const closeTracking = () => setTracking(null);

  // Render the tracking map once the modal + data are present.
  useEffect(() => {
    if (!tracking || !trackMapEl.current) return undefined;
    const map = L.map(trackMapEl.current, { center: [14.0, 121.0], zoom: 8, scrollWheelZoom: false });
    const group = L.layerGroup().addTo(map);
    const pts = [];
    tracking.assets.forEach((a) => {
      const color = a.assetType === 'tug' ? '#0d6efd' : '#198754';
      L.circleMarker([a.latitude, a.longitude], {
        radius: 9,
        color: '#fff',
        weight: 2,
        fillColor: color,
        fillOpacity: 1,
      })
        .bindTooltip(`${a.name} · ${a.speedKnots} kn · ${a.headingDeg}°`)
        .addTo(group);
      pts.push([a.latitude, a.longitude]);
    });
    if (pts.length === 1) map.setView(pts[0], 10);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [30, 30] });
    trackMapRef.current = map;
    const resizeTimer = setTimeout(() => map.invalidateSize(), 50);
    return () => {
      clearTimeout(resizeTimer);
      map.remove();
      trackMapRef.current = null;
    };
  }, [tracking]);

  const downloadSof = async () => {
    if (!tracking) return;
    setDownloading(true);
    try {
      const blob = await api.downloadSofPdf(tracking.bookingId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SoF-${tracking.bookingRef}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to download SoF PDF.' });
    } finally {
      setDownloading(false);
    }
  };

  const load = useCallback(async () => {
    const [refRes, listRes, recurRes] = await Promise.all([
      api.getBookingReference(),
      api.listBookings(),
      api.getRecurringBookings().catch(() => []),
    ]);
    setReference({ services: [], assistanceAreas: [], ...(refRes || { vessels: [], ports: [], berths: [], movementTypes: [] }) });
    setBookings(listRes || []);
    setRecurring(recurRes || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load bookings.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const berthsForPort = useMemo(
    () => reference.berths.filter((b) => !form.portId || b.port_id === form.portId),
    [reference.berths, form.portId]
  );
  // 8951: the assistance areas of the chosen port.
  const areasForPort = useMemo(
    () => (reference.assistanceAreas || []).filter((a) => a.port_id === form.portId),
    [reference.assistanceAreas, form.portId]
  );
  const selectedService = (reference.services || []).find((sv) => sv.id === form.serviceId) || null;

  const vesselOptions = isOffice ? customerVessels : reference.vessels;
  const recurVesselOptions = isOffice ? recurCustomerVessels : reference.vessels;
  // 8993: a standing order books a service and assistance area, like the booking form.
  const recurAreas = (reference.assistanceAreas || []).filter((a) => a.port_id === recur.portId);
  const recurService = (reference.services || []).find((sv) => sv.id === recur.serviceId) || null;
  // 9025: a standing order may name a berth of its port, like the booking form.
  const recurBerths = reference.berths.filter((b) => b.port_id === recur.portId);
  // 9020: options for the searchable customer / vessel dropdowns.
  const customerOptions = useMemo(() => toOptions(reference.customers), [reference.customers]);
  const vesselChoices = useMemo(() => toOptions(vesselOptions), [vesselOptions]);
  const recurVesselChoices = useMemo(() => toOptions(recurVesselOptions), [recurVesselOptions]);

  const openNew = () => {
    setErrors({});
    setQuote(null);
    setForm(EMPTY);
    setOverrides(NO_OVERRIDES);
    setConflict(null);
    setCustomerVessels([]);
    setModalOpen(true);
  };

  // Set form fields, clear their errors, and drop any quote — the tariff is
  // only shown for a complete, unchanged set of booking details (8845). 9022 /
  // 9005: a price override goes with the quote, and a changed booking may no
  // longer clash.
  const setField = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      delete next.form;
      delete next.indicativeOverride;
      delete next.indicativeOverrideReason;
      delete next.conflictOverrideReason;
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
    setQuote(null);
    setOverrides(NO_OVERRIDES);
    setConflict(null);
  };

  const setOverride = (patch) => {
    setOverrides((o) => ({ ...o, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      if ('overrideConflict' in patch) delete next.form;
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  const selectCustomer = async (customerUserId) => {
    setField({ customerUserId, vesselId: '' });
    setCustomerVessels([]);
    if (!customerUserId) return;
    try {
      const ref = await api.getBookingReference(customerUserId);
      setCustomerVessels(ref?.vessels || []);
    } catch (err) {
      setErrors((e) => ({ ...e, form: err.message || 'Failed to load the customer\u2019s vessels.' }));
    }
  };

  const suggestTrading = (vesselId, options = vesselOptions) =>
    tradingStatusForFlag(options.find((v) => v.id === vesselId)?.flag);

  const validate = () => {
    const next = {};
    if (isOffice && !form.customerUserId) next.customerUserId = 'Select a customer';
    if (!form.vesselId) next.vesselId = 'Select a vessel';
    if (!form.portId) next.portId = 'Select a port';
    // 9019: the target berth is optional.
    if (!form.serviceId) next.serviceId = 'Select a service';
    if (form.portId && areasForPort.length > 0 && !form.assistanceAreaId) {
      next.assistanceAreaId = 'Select the assistance area';
    }
    if (!form.tradingStatus) next.tradingStatus = 'Select a trading status';
    if (!form.scheduledAt) next.scheduledAt = 'Select a date/time';
    else if (new Date(form.scheduledAt).getTime() <= Date.now())
      next.scheduledAt = 'Date/time must be in the future';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const calculate = async () => {
    setQuote(null);
    if (!validate()) return;
    try {
      const q = await api.getBookingQuote({
        customerUserId: form.customerUserId || undefined,
        vesselId: form.vesselId || undefined,
        serviceId: form.serviceId || undefined,
        portId: form.portId || undefined,
        assistanceAreaId: form.assistanceAreaId || undefined,
      });
      setQuote(q);
    } catch (err) {
      setErrors((e) => ({ ...e, form: err.message || 'Failed to calculate tariff.' }));
    }
  };

  // 9022: an override is entered after the quote; 9005: booking over a clash needs a reason.
  const overriding = isOffice && Boolean(quote) && overrides.indicativeOverride !== '';
  const validateOverrides = () => {
    const next = {};
    if (overriding) {
      const amount = Number(overrides.indicativeOverride);
      if (!Number.isFinite(amount) || amount < 0) next.indicativeOverride = 'Enter a valid override amount';
      if (!overrides.indicativeOverrideReason.trim()) {
        next.indicativeOverrideReason = 'Enter the reason for the price override';
      }
    }
    if (conflict && overrides.overrideConflict && !overrides.conflictOverrideReason.trim()) {
      next.conflictOverrideReason = 'Enter the reason for booking over the conflict';
    }
    setErrors((e) => ({ ...e, ...next }));
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate() || !validateOverrides()) return;
    setSubmitting(true);
    setPageMsg(null);
    try {
      const created = await api.createBooking({
        ...(isOffice ? { customerUserId: form.customerUserId } : {}),
        vesselId: form.vesselId,
        portId: form.portId,
        berthId: form.berthId || null,
        serviceId: form.serviceId,
        assistanceAreaId: form.assistanceAreaId || null,
        tradingStatus: form.tradingStatus,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        remarks: form.remarks.trim() || null,
        ...(overriding
          ? {
              indicativeOverride: Number(overrides.indicativeOverride),
              indicativeOverrideReason: overrides.indicativeOverrideReason.trim(),
            }
          : {}),
        ...(conflict && overrides.overrideConflict
          ? { overrideConflict: true, conflictOverrideReason: overrides.conflictOverrideReason.trim() }
          : {}),
      });
      setModalOpen(false);
      // "Redirect to booking dashboard": refresh the active orders list in place.
      await load();
      setPageMsg({
        type: 'success',
        text: `Booking ${created.booking_ref} submitted — indicative rate ${money(created.indicative_rate, created.currency)}.`,
      });
    } catch (err) {
      // 9005: the vessel is already booked near this time.
      if (err.status === 409 && err.details?.conflict) setConflict(err.details);
      setErrors((p) => ({ ...p, form: err.message || 'Failed to submit booking.' }));
    } finally {
      setSubmitting(false);
    }
  };

  const openClearance = (booking) => {
    setClearanceError('');
    setClearance(booking);
    setClearanceForm({ aisArrivalWindow: '', captainReadiness: false });
  };

  const submitClearance = async (e) => {
    e.preventDefault();
    setClearanceError('');
    if (!clearanceForm.aisArrivalWindow) {
      setClearanceError('Enter the AIS estimated arrival window.');
      return;
    }
    setConfirming(true);
    try {
      const updated = await api.confirmBooking(clearance.id, {
        aisArrivalWindow: new Date(clearanceForm.aisArrivalWindow).toISOString(),
        captainReadiness: clearanceForm.captainReadiness,
      });
      setClearance(null);
      await load();
      setPageMsg({
        type: 'success',
        text: `Booking ${updated.booking_ref} confirmed — arrival clearance provided.`,
      });
    } catch (err) {
      setClearanceError(err.message || 'Failed to confirm arrival clearance.');
    } finally {
      setConfirming(false);
    }
  };

  const cancelBooking = async (b) => {
    const reason = window.prompt(
      `Cancel ${b.booking_ref}? A late-cancellation charge may apply per the tariff. Reason (optional):`,
      ''
    );
    if (reason === null) return; // user dismissed
    setPageMsg(null);
    try {
      const res = await api.cancelBooking(b.id, { type: 'cancellation', reason });
      await load();
      setPageMsg({
        type: res.charge ? 'warning' : 'success',
        text: res.charge
          ? `Booking ${b.booking_ref} cancelled — a ${res.charge.basis} cancellation charge of ${money(res.charge.amount, b.currency)} applies.`
          : `Booking ${b.booking_ref} cancelled — no charge (outside the late-cancellation window).`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to cancel booking.' });
    }
  };

  const selectRecurCustomer = async (customerUserId) => {
    setRecur((r) => ({ ...r, customerUserId, vesselId: '' }));
    setRecurCustomerVessels([]);
    setRecurMsg(null);
    if (!customerUserId) return;
    try {
      const ref = await api.getBookingReference(customerUserId);
      setRecurCustomerVessels(ref?.vessels || []);
    } catch (err) {
      setRecurMsg({ type: 'danger', text: err.message || 'Failed to load the customer\u2019s vessels.' });
    }
  };

  const submitRecurring = async (e) => {
    e.preventDefault();
    setRecurMsg(null);
    if (isOffice && !recur.customerUserId) {
      setRecurMsg({ type: 'danger', text: 'Select the customer this standing order is for.' });
      return;
    }
    if (!recur.serviceId) {
      setRecurMsg({ type: 'danger', text: 'Select the service this standing order books.' });
      return;
    }
    if (recur.portId && recurAreas.length > 0 && !recur.assistanceAreaId) {
      setRecurMsg({ type: 'danger', text: 'Select the assistance area.' });
      return;
    }
    if (!(Number(recur.intervalDays) > 0) || !recur.firstRun) {
      setRecurMsg({ type: 'danger', text: 'Interval (days) and first run date are required.' });
      return;
    }
    setSavingRecur(true);
    // 9025: berth (optional) and remarks, like the booking form; no pilotage or tug count.
    const payload = {
      vesselId: recur.vesselId || null,
      portId: recur.portId || null,
      berthId: recur.berthId || null,
      serviceId: recur.serviceId,
      assistanceAreaId: recur.assistanceAreaId || null,
      tradingStatus: recur.tradingStatus || null,
      remarks: recur.remarks.trim() || null,
      intervalDays: Number(recur.intervalDays),
      firstRun: new Date(recur.firstRun).toISOString(),
    };
    try {
      if (editingRecur) {
        await api.updateRecurringBooking(editingRecur.id, payload);
      } else {
        await api.createRecurringBooking({
          ...(isOffice ? { customerUserId: recur.customerUserId } : {}),
          ...payload,
        });
      }
      await load();
      setRecurMsg({
        type: 'success',
        text: editingRecur
          ? 'Standing order updated.'
          : 'Standing order saved — bookings auto-generate ahead of each run.',
      });
      if (editingRecur) {
        setEditingRecur(null);
        setRecur(EMPTY_RECUR);
        setRecurCustomerVessels([]);
      } else {
        setRecur({ ...recur, firstRun: '' });
      }
    } catch (err) {
      setRecurMsg({ type: 'danger', text: err.message || 'Failed to save standing order.' });
    } finally {
      setSavingRecur(false);
    }
  };

  // 9025: load a standing order into the form to edit it.
  const editRecurring = async (r) => {
    setRecurMsg(null);
    setEditingRecur(r);
    setRecur({
      customerUserId: r.customerUserId || '',
      vesselId: r.vesselId || '',
      portId: r.portId || '',
      berthId: r.berthId || '',
      serviceId: r.serviceId || '',
      assistanceAreaId: r.assistanceAreaId || '',
      tradingStatus: r.tradingStatus || '',
      remarks: r.remarks || '',
      intervalDays: String(r.intervalDays || 7),
      firstRun: toLocalInput(r.nextRun),
    });
    if (isOffice && r.customerUserId) {
      try {
        const ref = await api.getBookingReference(r.customerUserId);
        setRecurCustomerVessels(ref?.vessels || []);
      } catch (err) {
        setRecurMsg({ type: 'danger', text: err.message || 'Failed to load the customer’s vessels.' });
      }
    }
  };

  const cancelEditRecurring = () => {
    setEditingRecur(null);
    setRecur(EMPTY_RECUR);
    setRecurCustomerVessels([]);
    setRecurMsg(null);
  };

  const toggleRecurring = async (r) => {
    setRecurMsg(null);
    setRecurBusy(true);
    try {
      if (r.active) await api.pauseRecurringBooking(r.id);
      else await api.resumeRecurringBooking(r.id);
      await load();
      setRecurMsg({ type: 'success', text: r.active ? 'Standing order paused.' : 'Standing order resumed.' });
    } catch (err) {
      setRecurMsg({ type: 'danger', text: err.message || 'Failed to update the standing order.' });
    } finally {
      setRecurBusy(false);
    }
  };

  const deleteRecurring = async () => {
    if (!deletingRecur) return;
    setRecurBusy(true);
    try {
      await api.deleteRecurringBooking(deletingRecur.id);
      if (editingRecur?.id === deletingRecur.id) cancelEditRecurring();
      setDeletingRecur(null);
      await load();
      setRecurMsg({ type: 'success', text: 'Standing order deleted.' });
    } catch (err) {
      setDeletingRecur(null);
      setRecurMsg({ type: 'danger', text: err.message || 'Failed to delete the standing order.' });
    } finally {
      setRecurBusy(false);
    }
  };

  const approaching = bookings.filter((b) => b.ais_near_port && canClear(b));

  return (
    <div className="container-fluid py-4" data-testid="booking-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-ship me-2" aria-hidden="true" />
                Customer Booking &amp; Live Dashboard
              </h3>
              <p className="text-muted mb-0 small">Book towage services and track your active orders.</p>
            </div>
            <button className="btn btn-primary" onClick={openNew} data-testid="new-booking">
              <i className="fas fa-plus me-1" aria-hidden="true" />
              New Booking Request
            </button>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="booking-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="booking-message">
          {pageMsg.text}
        </div>
      )}

      {approaching.length > 0 && (
        <div className="alert alert-warning d-flex align-items-start" data-testid="ais-notifications">
          <i className="fas fa-satellite-dish fa-lg me-3 mt-1" aria-hidden="true" />
          <div className="flex-grow-1">
            <strong>Vessels approaching (AIS geofence)</strong>
            <ul className="mb-0 small mt-1">
              {approaching.map((b) => (
                <li key={b.id} className="d-flex justify-content-between align-items-center py-1">
                  <span>
                    {b.booking_ref} — {b.vessel_name || 'vessel'} inbound to {b.port_name || 'port'}; provide
                    final arrival clearance.
                  </span>
                  <button
                    className="btn btn-sm btn-warning ms-3"
                    onClick={() => openClearance(b)}
                    data-testid="notification-clearance"
                  >
                    Provide Final Clearance
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white pt-4 border-bottom-0">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-list-ul me-2" aria-hidden="true" />
            Active Towage Orders
          </h5>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Booking</th>
                    {isOffice && <th>Customer</th>}
                    <th>Vessel</th>
                    <th>Service</th>
                    <th>Port / Berth</th>
                    <th>Scheduled</th>
                    <th>Tugs</th>
                    {/* 9021: no Pilotage column — the form no longer asks for it. */}
                    <th>Indicative</th>
                    <th>Status</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (
                    <tr>
                      <td colSpan={isOffice ? 10 : 9} className="text-center text-muted py-4">
                        No bookings yet.
                      </td>
                    </tr>
                  )}
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid="booking-row" data-ref={b.booking_ref}>
                      <td>
                        <span className="fw-bold">{b.booking_ref}</span>
                        {/* 9023: the booking's remarks. */}
                        {b.remarks && (
                          <span className="d-block small text-muted text-wrap" style={{ maxWidth: 220 }} data-testid="booking-row-remarks">
                            {b.remarks}
                          </span>
                        )}
                      </td>
                      {isOffice && <td data-testid="booking-customer">{b.customer_name || '—'}</td>}
                      <td>{b.vessel_name || '—'}</td>
                      <td className="small" data-testid="booking-row-service">
                        {b.service_name || b.movement_type}
                      </td>
                      <td className="small">
                        {b.port_name || '—'}
                        {b.berth_name ? ` / ${b.berth_name}` : ''}
                        {b.assistance_area_name && (
                          <span className="d-block text-muted" data-testid="booking-row-area">
                            {b.assistance_area_name}
                          </span>
                        )}
                      </td>
                      <td className="small">{fmt(b.scheduled_at)}</td>
                      <td className="small" data-testid="booking-tugs">
                        {tugsLabel(b)}
                      </td>
                      <td data-testid="booking-indicative">
                        {money(b.indicative_rate, b.currency)}
                        {/* 9022: office staff see that the price was overridden, and why. */}
                        {isOffice && b.indicative_overridden && (
                          <span
                            className="d-block small text-warning-emphasis"
                            title={`System quote ${money(b.indicative_quoted_rate, b.currency)} — overridden${
                              b.indicative_overridden_by ? ` by ${b.indicative_overridden_by}` : ''
                            }: ${b.indicative_override_reason}`}
                            data-testid="booking-price-overridden"
                          >
                            <i className="fas fa-pen-to-square me-1" aria-hidden="true" />
                            overridden (quote {money(b.indicative_quoted_rate, b.currency)})
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge(b.status)}`} data-testid="booking-status">
                          {b.status}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-1">
                          {b.status === 'Submitted' && canClear(b) && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openClearance(b)}
                              data-testid="provide-clearance"
                            >
                              <i className="fas fa-clipboard-check me-1" aria-hidden="true" />
                              Provide Final Clearance
                            </button>
                          )}
                          {b.status !== 'Submitted' && isOwn(b) && (
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => openTracking(b)}
                              data-testid="track-execution"
                            >
                              <i className="fas fa-satellite-dish me-1" aria-hidden="true" />
                              Track Live Execution
                            </button>
                          )}
                          {!['Completed', 'Closed', 'Cancelled'].includes(b.status) && canCancel(b) && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => cancelBooking(b)}
                              data-testid="cancel-booking"
                            >
                              <i className="fas fa-ban me-1" aria-hidden="true" />
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Standing orders (FR-BKG-5) */}
      <div className="card border-0 shadow-sm mb-4" data-testid="standing-orders">
        <div className="card-header bg-white pt-4 border-bottom-0">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-repeat me-2" aria-hidden="true" />
            Standing Orders (Recurring Bookings)
          </h5>
          <p className="text-muted small mb-0">
            A recurring schedule auto-generates bookings ahead of each run (e.g. weekly liner calls).
          </p>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-lg-5 mb-3">
              {recurMsg && (
                <div className={`alert alert-${recurMsg.type} py-2`} role="alert" data-testid="recur-message">
                  {recurMsg.text}
                </div>
              )}
              <form onSubmit={submitRecurring} noValidate className="border rounded p-3" data-testid="recur-form">
                {editingRecur && (
                  <div className="d-flex justify-content-between align-items-center mb-2" data-testid="recur-editing">
                    <span className="small fw-bold text-primary">
                      <i className="fas fa-pen me-1" aria-hidden="true" />
                      Editing standing order{editingRecur.vesselName ? ` — ${editingRecur.vesselName}` : ''}
                    </span>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={cancelEditRecurring} data-testid="recur-cancel-edit">
                      Cancel edit
                    </button>
                  </div>
                )}
                {isOffice && (
                  <div className="mb-2">
                    <label htmlFor="recur-customer" className="form-label small fw-bold text-muted">
                      Customer <span className="text-danger">*</span>
                    </label>
                    {/* 9020: type to filter; 9025: a standing order keeps its customer when edited. */}
                    <SearchableSelect
                      id="recur-customer"
                      value={recur.customerUserId}
                      onChange={selectRecurCustomer}
                      options={customerOptions}
                      placeholder="Search the customer this standing order is for…"
                      disabled={Boolean(editingRecur)}
                      testId="recur-customer"
                    />
                  </div>
                )}
                <div className="mb-2">
                  <label htmlFor="recur-vessel" className="form-label small fw-bold text-muted">Vessel</label>
                  <SearchableSelect
                    id="recur-vessel"
                    value={recur.vesselId}
                    onChange={(vesselId) =>
                      setRecur((r) => ({
                        ...r,
                        vesselId,
                        tradingStatus: suggestTrading(vesselId, recurVesselOptions) || r.tradingStatus,
                      }))
                    }
                    options={recurVesselChoices}
                    placeholder={isOffice && !recur.customerUserId ? 'Select a customer first…' : 'Search a vessel…'}
                    disabled={isOffice && !recur.customerUserId}
                    testId="recur-vessel"
                  />
                </div>
                <div className="mb-2">
                  <label htmlFor="recur-service" className="form-label small fw-bold text-muted">
                    Service <span className="text-danger">*</span>
                  </label>
                  <select id="recur-service" className="form-select form-select-sm" value={recur.serviceId}
                    onChange={(e) => setRecur({ ...recur, serviceId: e.target.value })} data-testid="recur-service">
                    <option value="">Select a service…</option>
                    {(reference.services || []).map((sv) => <option key={sv.id} value={sv.id}>{sv.name}</option>)}
                  </select>
                  {recurService && (
                    <div className="form-text" data-testid="recur-jobs-preview">
                      Jobs: {jobsPreview(recurService)}. Each job is done by one tug.
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-6 mb-2">
                    <label htmlFor="recur-port" className="form-label small fw-bold text-muted">Port</label>
                    <select id="recur-port" className="form-select form-select-sm" value={recur.portId}
                      onChange={(e) => setRecur({ ...recur, portId: e.target.value, berthId: '', assistanceAreaId: '' })} data-testid="recur-port">
                      <option value="">Select…</option>
                      {reference.ports.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="col-6 mb-2">
                    <label htmlFor="recur-area" className="form-label small fw-bold text-muted">
                      Assistance Area {recurAreas.length > 0 && <span className="text-danger">*</span>}
                    </label>
                    <select id="recur-area" className="form-select form-select-sm" value={recur.assistanceAreaId}
                      onChange={(e) => setRecur({ ...recur, assistanceAreaId: e.target.value })}
                      disabled={!recur.portId || recurAreas.length === 0} data-testid="recur-area">
                      <option value="">
                        {!recur.portId ? 'Select a port first…' : recurAreas.length === 0 ? 'No areas for this port' : 'Select…'}
                      </option>
                      {recurAreas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-2">
                  <label htmlFor="recur-berth" className="form-label small fw-bold text-muted">Berth (optional)</label>
                  <select id="recur-berth" className="form-select form-select-sm" value={recur.berthId}
                    onChange={(e) => setRecur({ ...recur, berthId: e.target.value })}
                    disabled={!recur.portId} data-testid="recur-berth">
                    <option value="">{!recur.portId ? 'Select a port first…' : 'No specific berth'}</option>
                    {recurBerths.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="mb-2">
                  <label htmlFor="recur-trading" className="form-label small fw-bold text-muted">Trading Status</label>
                  <select id="recur-trading" className="form-select form-select-sm" value={recur.tradingStatus}
                    onChange={(e) => setRecur({ ...recur, tradingStatus: e.target.value })} data-testid="recur-trading">
                    <option value="">Select…</option>
                    {TRADING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="row">
                  <div className="col-4 mb-2">
                    <label htmlFor="recur-interval" className="form-label small fw-bold text-muted">Every (days)</label>
                    <input id="recur-interval" type="number" min="1" className="form-control form-control-sm"
                      value={recur.intervalDays} onChange={(e) => setRecur({ ...recur, intervalDays: e.target.value })} data-testid="recur-interval" />
                  </div>
                  <div className="col-8 mb-2">
                    <label htmlFor="recur-first" className="form-label small fw-bold text-muted">First Run</label>
                    <input id="recur-first" type="datetime-local" className="form-control form-control-sm"
                      value={recur.firstRun} onChange={(e) => setRecur({ ...recur, firstRun: e.target.value })} data-testid="recur-first" />
                  </div>
                </div>
                <div className="mb-2">
                  <label htmlFor="recur-remarks" className="form-label small fw-bold text-muted">Remarks</label>
                  <textarea id="recur-remarks" className="form-control form-control-sm" rows={2} maxLength={1000}
                    value={recur.remarks} onChange={(e) => setRecur({ ...recur, remarks: e.target.value })}
                    placeholder={REMARKS_PLACEHOLDER} data-testid="recur-remarks" />
                </div>
                <button type="submit" className="btn btn-primary btn-sm w-100" disabled={savingRecur} data-testid="save-recurring">
                  {savingRecur && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                  <i className="fas fa-repeat me-1" aria-hidden="true" />
                  {editingRecur ? 'Update Standing Order' : 'Save Standing Order'}
                </button>
              </form>
            </div>
            <div className="col-lg-7">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0 small" data-testid="recurring-table">
                  <thead className="bg-light text-secondary text-uppercase">
                    <tr>
                      {isOffice && <th>Customer</th>}<th>Vessel</th><th>Port / Berth / Area</th><th>Service</th>
                      <th className="text-center">Every</th><th>Next Run</th><th>Status</th><th aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {recurring.length === 0 ? (
                      <tr><td colSpan={isOffice ? 8 : 7} className="text-center text-muted py-3" data-testid="recurring-empty">No standing orders.</td></tr>
                    ) : (
                      recurring.map((r) => (
                        <tr
                          key={r.id}
                          data-testid="recurring-row"
                          data-id={r.id}
                          data-active={r.active ? 'true' : 'false'}
                          className={editingRecur?.id === r.id ? 'table-primary' : undefined}
                        >
                          {isOffice && <td data-testid="recurring-customer">{r.customerName || '—'}</td>}
                          <td className="fw-semibold">{r.vesselName || '—'}</td>
                          <td>
                            {r.portName || '—'}
                            {r.berthName && <span data-testid="recurring-berth"> / {r.berthName}</span>}
                            {r.assistanceAreaName && <span className="d-block text-muted">{r.assistanceAreaName}</span>}
                          </td>
                          <td>
                            <span data-testid="recurring-service">{r.serviceName || r.movementType}</span>
                            {r.remarks && (
                              <span className="d-block text-muted text-wrap" data-testid="recurring-remarks">{r.remarks}</span>
                            )}
                          </td>
                          <td className="text-center" data-testid="recurring-interval">{r.intervalDays}d</td>
                          <td>{r.nextRun ? new Date(r.nextRun).toLocaleDateString() : '—'}</td>
                          <td>
                            <span className={`badge ${r.active ? 'bg-success' : 'bg-secondary'}`} data-testid="recurring-status">
                              {r.active ? 'Active' : 'Paused'}
                            </span>
                          </td>
                          {/* 9025: edit, pause / resume and delete. */}
                          <td className="text-end text-nowrap">
                            <button type="button" className="btn btn-sm btn-outline-primary me-1" title="Edit"
                              aria-label="Edit standing order" onClick={() => editRecurring(r)} disabled={recurBusy}
                              data-testid="recurring-edit">
                              <i className="fas fa-pen" aria-hidden="true" />
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary me-1"
                              title={r.active ? 'Pause' : 'Resume'} aria-label={r.active ? 'Pause standing order' : 'Resume standing order'}
                              onClick={() => toggleRecurring(r)} disabled={recurBusy} data-testid="recurring-toggle">
                              <i className={`fas ${r.active ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" title="Delete"
                              aria-label="Delete standing order" onClick={() => setDeletingRecur(r)} disabled={recurBusy}
                              data-testid="recurring-delete">
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(deletingRecur)}
        title="Delete standing order"
        message={
          deletingRecur
            ? `Delete the standing order for ${deletingRecur.vesselName || 'this vessel'} (${
                deletingRecur.serviceName || deletingRecur.movementType
              }, every ${deletingRecur.intervalDays} days)? Bookings it already generated are kept.`
            : ''
        }
        busy={recurBusy}
        onConfirm={deleteRecurring}
        onCancel={() => setDeletingRecur(null)}
      />

      {/* Live execution tracking modal */}
      {(tracking || trackingLoading) && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="tracking-modal">
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-satellite-dish me-2 text-info" aria-hidden="true" />
                    Live Execution — {tracking?.bookingRef || ''}
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeTracking} />
                </div>
                <div className="modal-body">
                  {trackingLoading || !tracking ? (
                    <div className="d-flex justify-content-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading…</span>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-lg-7 mb-3">
                        <h6 className="text-secondary small text-uppercase">
                          Live Tracking Map — {tracking.vesselName || 'Vessel'}
                        </h6>
                        <div
                          ref={trackMapEl}
                          data-testid="tracking-map"
                          style={{ height: 340, width: '100%', borderRadius: 8, background: '#0e1e38' }}
                        />
                        <div className="small text-muted mt-2">
                          <span className="badge bg-success me-1">&nbsp;</span> Vessel (AIS)
                          <span className="badge bg-primary ms-3 me-1">&nbsp;</span> Assigned tug (GPS)
                          {tracking.assignedTugs.length > 0 && (
                            <span className="ms-2" data-testid="assigned-tugs">
                              {tracking.assignedTugs.map((t) => t.name).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-5 mb-3">
                        <h6 className="text-secondary small text-uppercase">Execution Milestones</h6>
                        <ul className="list-group list-group-flush" data-testid="milestone-ladder">
                          {tracking.milestones.map((m) => (
                            <li
                              key={m.type}
                              className="list-group-item d-flex justify-content-between align-items-center px-0"
                              data-testid="milestone"
                              data-type={m.type}
                              data-reached={m.reached ? 'true' : 'false'}
                            >
                              <span>
                                <i
                                  className={`fas ${m.reached ? 'fa-circle-check text-success' : 'fa-circle text-muted'} me-2`}
                                  aria-hidden="true"
                                />
                                {m.label}
                              </span>
                              <span className="small text-muted">{m.loggedAt ? fmt(m.loggedAt) : 'Pending'}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="mt-3">
                          {tracking.sof.signed ? (
                            <div data-testid="sof-available">
                              <p className="small text-success mb-2">
                                <i className="fas fa-signature me-1" aria-hidden="true" />
                                SoF e-signed by {tracking.sof.signedBy}.
                              </p>
                              <button
                                className="btn btn-success w-100"
                                onClick={downloadSof}
                                disabled={downloading}
                                data-testid="download-sof"
                              >
                                {downloading && (
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                )}
                                <i className="fas fa-file-pdf me-1" aria-hidden="true" />
                                Download Statement of Facts PDF
                              </button>
                            </div>
                          ) : (
                            <p className="small text-muted" data-testid="sof-pending">
                              The Statement of Facts will be available to download once the captain
                              finalizes and signs it.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={closeTracking}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* New booking modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="booking-modal">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={submit} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-anchor me-2 text-primary" aria-hidden="true" />
                      New Booking Request
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && <div className="alert alert-danger">{errors.form}</div>}
                    {isOffice && (
                      <div className="mb-3">
                        <label htmlFor="b-customer" className="form-label small fw-bold text-muted">
                          Customer <span className="text-danger">*</span>
                        </label>
                        {/* 9020: type to filter the customers. */}
                        <SearchableSelect
                          id="b-customer"
                          value={form.customerUserId}
                          onChange={selectCustomer}
                          options={customerOptions}
                          placeholder="Search the customer this booking is for…"
                          invalid={Boolean(errors.customerUserId)}
                          testId="booking-customer-select"
                        />
                        {errors.customerUserId && <div className="invalid-feedback d-block">{errors.customerUserId}</div>}
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-vessel" className="form-label small fw-bold text-muted">
                          Vessel <span className="text-danger">*</span>
                        </label>
                        <SearchableSelect
                          id="b-vessel"
                          value={form.vesselId}
                          onChange={(vesselId) =>
                            setField({
                              vesselId,
                              tradingStatus: suggestTrading(vesselId) || form.tradingStatus,
                            })
                          }
                          options={vesselChoices}
                          placeholder={isOffice && !form.customerUserId ? 'Select a customer first…' : 'Search a vessel…'}
                          disabled={isOffice && !form.customerUserId}
                          invalid={Boolean(errors.vesselId)}
                          noMatchesText={vesselChoices.length ? 'No matching vessel' : 'No vessels to book'}
                          testId="booking-vessel"
                        />
                        {errors.vesselId && <div className="invalid-feedback d-block">{errors.vesselId}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-service" className="form-label small fw-bold text-muted">
                          Service <span className="text-danger">*</span>
                        </label>
                        <select
                          id="b-service"
                          className={`form-select form-select-sm${errors.serviceId ? ' is-invalid' : ''}`}
                          value={form.serviceId}
                          onChange={(e) => setField({ serviceId: e.target.value })}
                          data-testid="booking-service"
                        >
                          <option value="">Select a service…</option>
                          {(reference.services || []).map((sv) => (
                            <option key={sv.id} value={sv.id}>
                              {sv.name}
                            </option>
                          ))}
                        </select>
                        {errors.serviceId && <div className="invalid-feedback">{errors.serviceId}</div>}
                      </div>
                    </div>
                    {selectedService && (
                      <div className="alert alert-light border small py-2" data-testid="booking-jobs-preview">
                        <i className="fas fa-list-check me-1 text-primary" aria-hidden="true" />
                        <strong>Jobs:</strong> {jobsPreview(selectedService)}. Each job is done by
                        one tug.
                        {selectedService.description && <span className="d-block text-muted">{selectedService.description}</span>}
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-port" className="form-label small fw-bold text-muted">
                          Target Port <span className="text-danger">*</span>
                        </label>
                        <select
                          id="b-port"
                          className={`form-select form-select-sm${errors.portId ? ' is-invalid' : ''}`}
                          value={form.portId}
                          onChange={(e) => setField({ portId: e.target.value, berthId: '', assistanceAreaId: '' })}
                          data-testid="booking-port"
                        >
                          <option value="">Select a port…</option>
                          {reference.ports.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                        {errors.portId && <div className="invalid-feedback">{errors.portId}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-berth" className="form-label small fw-bold text-muted">
                          Target Berth <span className="text-muted fw-normal">(optional)</span>
                        </label>
                        {/* 9019: the berth is optional. */}
                        <select
                          id="b-berth"
                          className={`form-select form-select-sm${errors.berthId ? ' is-invalid' : ''}`}
                          value={form.berthId}
                          onChange={(e) => setField({ berthId: e.target.value })}
                          disabled={!form.portId}
                          data-testid="booking-berth"
                        >
                          <option value="">{form.portId ? 'No specific berth' : 'Select a port first…'}</option>
                          {berthsForPort.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                        {errors.berthId && <div className="invalid-feedback">{errors.berthId}</div>}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-area" className="form-label small fw-bold text-muted">
                          Assistance Area {areasForPort.length > 0 && <span className="text-danger">*</span>}
                        </label>
                        <select
                          id="b-area"
                          className={`form-select form-select-sm${errors.assistanceAreaId ? ' is-invalid' : ''}`}
                          value={form.assistanceAreaId}
                          onChange={(e) => setField({ assistanceAreaId: e.target.value })}
                          disabled={!form.portId || areasForPort.length === 0}
                          data-testid="booking-area"
                        >
                          <option value="">
                            {!form.portId
                              ? 'Select a port first…'
                              : areasForPort.length === 0
                                ? 'No areas set up for this port'
                                : 'Select an assistance area…'}
                          </option>
                          {areasForPort.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                        {errors.assistanceAreaId && <div className="invalid-feedback">{errors.assistanceAreaId}</div>}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-datetime" className="form-label small fw-bold text-muted">
                          Scheduled Date/Time <span className="text-danger">*</span>
                        </label>
                        <input
                          id="b-datetime"
                          type="datetime-local"
                          className={`form-control form-control-sm${errors.scheduledAt ? ' is-invalid' : ''}`}
                          value={form.scheduledAt}
                          onChange={(e) => setField({ scheduledAt: e.target.value })}
                          data-testid="booking-datetime"
                        />
                        {errors.scheduledAt && <div className="invalid-feedback">{errors.scheduledAt}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label htmlFor="b-trading" className="form-label small fw-bold text-muted">
                          Trading Status <span className="text-danger">*</span>
                        </label>
                        <select
                          id="b-trading"
                          className={`form-select form-select-sm${errors.tradingStatus ? ' is-invalid' : ''}`}
                          value={form.tradingStatus}
                          onChange={(e) => setField({ tradingStatus: e.target.value })}
                          data-testid="booking-trading"
                        >
                          <option value="">Select…</option>
                          {TRADING_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {errors.tradingStatus && <div className="invalid-feedback">{errors.tradingStatus}</div>}
                      </div>
                    </div>

                    {/* 9023: free-text remarks replace "Tugs per Job"; 9021: no Pilotage Required switch. */}
                    <div className="mb-3">
                      <label htmlFor="b-remarks" className="form-label small fw-bold text-muted">
                        Remarks
                      </label>
                      <textarea
                        id="b-remarks"
                        className="form-control form-control-sm"
                        rows={2}
                        maxLength={1000}
                        value={form.remarks}
                        // Remarks do not change the price, so the quote stays.
                        onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                        placeholder={REMARKS_PLACEHOLDER}
                        data-testid="booking-remarks"
                      />
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={calculate}
                        data-testid="calculate-tariff"
                      >
                        <i className="fas fa-calculator me-1" aria-hidden="true" />
                        Calculate Indicative Tariff
                      </button>
                      {quote && (
                        <span className="badge bg-success p-2" data-testid="indicative-rate">
                          Indicative: {money(quote.indicativeRate, quote.currency)}
                        </span>
                      )}
                    </div>
                    {quote && (
                      <p className="small text-muted mt-2 mb-0" data-testid="indicative-basis">
                        {quote.serviceRate
                          ? `${quote.serviceRate.rateBasisLabel} — ${quote.serviceRate.serviceName}, ${quote.serviceRate.portName}${
                              quote.serviceRate.areaName ? ` (${quote.serviceRate.areaName})` : ''
                            }: ${money(quote.serviceRate.amount, quote.serviceRate.currency)}${
                              quote.serviceRate.rateBasis === 'HourlyPerTug'
                                ? ` per tug-hour × ${quote.jobs.length} job(s), est. ${quote.estimatedHours}h${
                                    quote.serviceRate.minHours ? ` (min ${quote.serviceRate.minHours}h)` : ''
                                  }`
                                : ''
                            }, VAT ${quote.serviceRate.vatPercent}%.`
                          : `Hourly rate card${quote.rateCard ? ` ${quote.rateCard.name}` : ''} × ${quote.jobs?.length || 1} tug(s), est. ${quote.estimatedHours}h. No service tariff is set for this port and area.`}
                      </p>
                    )}

                    {/* 9022: office staff may override the indicative price, with a reason. */}
                    {isOffice && quote && (
                      <div className="border rounded p-2 mt-3 bg-light" data-testid="price-override">
                        <div className="small fw-bold text-muted mb-2">
                          <i className="fas fa-pen-to-square me-1" aria-hidden="true" />
                          Override indicative price (optional)
                        </div>
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label htmlFor="b-override" className="form-label small text-muted mb-1">
                              Override amount ({quote.currency})
                            </label>
                            <input
                              id="b-override"
                              type="number"
                              min="0"
                              step="0.01"
                              className={`form-control form-control-sm${errors.indicativeOverride ? ' is-invalid' : ''}`}
                              value={overrides.indicativeOverride}
                              onChange={(e) => setOverride({ indicativeOverride: e.target.value })}
                              placeholder={String(quote.indicativeRate ?? '')}
                              data-testid="price-override-amount"
                            />
                            {errors.indicativeOverride && (
                              <div className="invalid-feedback">{errors.indicativeOverride}</div>
                            )}
                          </div>
                          <div className="col-md-8">
                            <label htmlFor="b-override-reason" className="form-label small text-muted mb-1">
                              Reason {overrides.indicativeOverride !== '' && <span className="text-danger">*</span>}
                            </label>
                            <input
                              id="b-override-reason"
                              type="text"
                              maxLength={1000}
                              className={`form-control form-control-sm${errors.indicativeOverrideReason ? ' is-invalid' : ''}`}
                              value={overrides.indicativeOverrideReason}
                              onChange={(e) => setOverride({ indicativeOverrideReason: e.target.value })}
                              placeholder="e.g. negotiated contract rate"
                              data-testid="price-override-reason"
                            />
                            {errors.indicativeOverrideReason && (
                              <div className="invalid-feedback">{errors.indicativeOverrideReason}</div>
                            )}
                          </div>
                        </div>
                        {overrides.indicativeOverride !== '' && (
                          <div className="small text-muted mt-1" data-testid="price-override-summary">
                            The booking will be saved at {money(overrides.indicativeOverride, quote.currency)} instead of the
                            quoted {money(quote.indicativeRate, quote.currency)}.
                          </div>
                        )}
                      </div>
                    )}

                    {/* 9005: the vessel is already booked near this time. */}
                    {conflict && (
                      <div className="alert alert-warning mt-3 mb-0 small" data-testid="booking-conflict">
                        <div>
                          <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                          <strong>Schedule conflict:</strong> {conflict.conflict.vesselName || 'This vessel'} already has
                          booking <strong data-testid="booking-conflict-ref">{conflict.conflict.bookingRef}</strong> at{' '}
                          {fmt(conflict.conflict.scheduledAt)} ({conflict.conflict.status}), within {conflict.windowHours}{' '}
                          hours of this one.
                        </div>
                        {conflict.canOverride ? (
                          <>
                            <div className="form-check mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="b-override-conflict"
                                checked={overrides.overrideConflict}
                                onChange={(e) => setOverride({ overrideConflict: e.target.checked })}
                                data-testid="booking-conflict-override"
                              />
                              <label className="form-check-label fw-bold" htmlFor="b-override-conflict">
                                Book anyway
                              </label>
                            </div>
                            {overrides.overrideConflict && (
                              <div className="mt-2">
                                <label htmlFor="b-conflict-reason" className="form-label small mb-1">
                                  Reason <span className="text-danger">*</span>
                                </label>
                                <input
                                  id="b-conflict-reason"
                                  type="text"
                                  maxLength={1000}
                                  className={`form-control form-control-sm${errors.conflictOverrideReason ? ' is-invalid' : ''}`}
                                  value={overrides.conflictOverrideReason}
                                  onChange={(e) => setOverride({ conflictOverrideReason: e.target.value })}
                                  placeholder="e.g. second movement for the same call"
                                  data-testid="booking-conflict-reason"
                                />
                                {errors.conflictOverrideReason && (
                                  <div className="invalid-feedback">{errors.conflictOverrideReason}</div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="mt-1">Choose another time, or contact the booking desk.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} data-testid="submit-booking">
                      {submitting && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-paper-plane me-1" aria-hidden="true" />
                      Submit Booking Request
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      {/* Final Arrival Confirmation modal */}
      {clearance && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="clearance-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={submitClearance} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-clipboard-check me-2 text-primary" aria-hidden="true" />
                      Final Arrival Confirmation — {clearance.booking_ref}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setClearance(null)} />
                  </div>
                  <div className="modal-body">
                    {clearanceError && <div className="alert alert-danger">{clearanceError}</div>}
                    <div className="mb-3">
                      <label htmlFor="c-window" className="form-label small fw-bold text-muted">
                        AIS Estimated Arrival Window <span className="text-danger">*</span>
                      </label>
                      <input
                        id="c-window"
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={clearanceForm.aisArrivalWindow}
                        onChange={(e) => setClearanceForm({ ...clearanceForm, aisArrivalWindow: e.target.value })}
                        data-testid="arrival-window"
                      />
                    </div>
                    <div className="form-check form-switch mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="c-captain"
                        checked={clearanceForm.captainReadiness}
                        onChange={(e) => setClearanceForm({ ...clearanceForm, captainReadiness: e.target.checked })}
                        data-testid="captain-readiness"
                      />
                      <label className="form-check-label small fw-bold text-muted" htmlFor="c-captain">
                        Captain Readiness Confirmed
                      </label>
                    </div>
                    {/* 9026: no Pilotage Required switch — the booking keeps its value. */}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setClearance(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={confirming} data-testid="confirm-clearance">
                      {confirming && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-check me-1" aria-hidden="true" />
                      Confirm Arrival Clearance
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
